import "./App.scss";
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import { ApolloClient, ApolloProvider, InMemoryCache } from "@apollo/client";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { ToastContainer } from "react-toastify";
import { kc, authEnabled, initializeKeycloak, isRestrictedRoleOnly } from './keycloakConfig';
import Dashboard from "./Dashboard/Dashboard";
import Analytics from "./Analytics/Analytics";
import DeviceGroup from './Devices/DeviceGroup';
import Catalogs from './Catalogs/Catalogs';
import JobStatusTable from "./JobModule/JobGrid"
import CatalogDeviceCategory from "./Catalogs/CatalogDeviceCategory";
import DeviceModelGroup from './DeviceModelGroup/DeviceModelGroup';
import DeviceModel from './DeviceModel/DeviceModel';
import Usergrid from "./Usermodule/Usergrid";
import Updateuserdetails from "./Usermodule/Updateuserdetails";
import UpdateUserProfile from "./Usermodule/UpdateUserProfile";
import Devices from "./Devices/Devices";
import HubDevices from "./Devices/HubDevices";
import DeviceHealthCheck from "./Devices/DeviceHealthCheck";
import MqttTestClient from "./Devices/MqttTestClient";
import RemoteTerminal from "./Devices/RemoteTerminal";
import FirmwareDetailsGrid from "./Ota/FirmwareDetailsGrid";
import IotJobsGrid from "./Ota/IotJobsGrid";
import IotJobStatusGrid from "./Ota/Iot Jobs/IotJobStatusGrid";
import OtaDashboard from "./Ota/OtaDashboard";
import OtaArtifacts from "./Ota/Artifacts/OtaArtifacts";
import Campaigns from "./Ota/Campaigns/Campaigns";
import React from "react";
import "./assets/css/Loader.css";
import DevicePolicy from "./DevicePolicyModule/DevicePolicy";
import IrModels from "./IrModels/IrModels";
import UploadIrJson from "./IrModels/UploadIrJson";
import AnalyticsChat from "./AnalyticsChat/AnalyticsChat";

const client = new ApolloClient({
  uri: process.env.REACT_APP_GRAPHQL_URL,
  cache: new InMemoryCache(),
});

// OPERATOR-only and QA-only users are restricted to these routes; direct navigation elsewhere redirects here.
const RESTRICTED_ROLE_ALLOWED_PATHS = ["/ota/artifacts", "/ota/campaigns", "/devices", "/devices/hubs", "/remote-terminal"];

// Only ADMIN users may open these routes; anyone else is sent to the home page.
function AppLayout() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isTerminalPopout =
    location.pathname === "/remote-terminal" &&
    searchParams.get("popout") === "1";

  if (isRestrictedRoleOnly() && !RESTRICTED_ROLE_ALLOWED_PATHS.includes(location.pathname)) {
    return <Navigate to="/ota/artifacts" replace />;
  }

  return (
    <>
      {!isTerminalPopout && <Header kc={kc} />}
      <div className={isTerminalPopout ? "" : "d-flex"}>
        {!isTerminalPopout && <Sidebar />}
        <div
          className={isTerminalPopout ? "" : "body-layout"}
          style={isTerminalPopout ? { width: "100%", height: "100vh" } : undefined}
        >
          <ApolloProvider client={client}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/devices" element={<Devices />} />
              <Route path="/devices/hubs" element={<HubDevices />} />
              <Route path="/devices/health" element={<DeviceHealthCheck />} />
              <Route path="/devices/health/:thingName" element={<DeviceHealthCheck />} />
              <Route path="/devices/mqtt-test-client" element={<MqttTestClient />} />
              <Route path="/remote-terminal" element={<RemoteTerminal />} />
              <Route path="/device-groups/*" element={<DeviceGroup />} />
              <Route path="/device-policies/*" element={<DevicePolicy />} />
              {authEnabled && <Route path="/catalogs" element={<Catalogs />} />}
              <Route path="/catalogs/device-categories" element={<CatalogDeviceCategory />} />
              <Route path="/device-jobs" element={<JobStatusTable />} />
              <Route path="/catalogs/device-model-groups" element={<DeviceModelGroup />} />
              <Route path="/catalogs/device-models" element={<DeviceModel />} />
              <Route path="/users" element={<Navigate to="/users/web" replace />} />
              {/* key makes each list start fresh (page 1, no search) when switching between the two */}
              <Route path="/users/web" element={<Usergrid key="web" userType="web" />} />
              <Route path="/users/mobile" element={<Usergrid key="mobile" userType="mobile" />} />
              <Route path="/users/update" element={<Updateuserdetails />} />
              <Route path="/user-profile/update" element={<UpdateUserProfile />} />
              <Route path="/firmwares" element={<FirmwareDetailsGrid />} />
              <Route path="/ota/iot-jobs" element={<IotJobsGrid />} />
              <Route path="/list/:jobId" element={<IotJobStatusGrid />} />
              <Route path="/otaDashboard/:jobId" element={<OtaDashboard />} />
              <Route path="/ota/artifacts" element={<OtaArtifacts />} />
              <Route path="/ota/campaigns" element={<Campaigns />} />
              <Route path="/ir-models" element={<IrModels />} />
              <Route path="/ir-models/upload" element={<UploadIrJson />} />
              <Route path="/analytics-chat" element={<AnalyticsChat />} />
            </Routes>
          </ApolloProvider>
        </div>
      </div>
    </>
  );
}

function App() {
  const [isInitialized, setIsInitialized] = React.useState(false);
  React.useEffect(() => {
    initializeKeycloak(() => setIsInitialized(true)).catch((error) => {
      console.error("Failed to initialize Keycloak:", error);
    });
  }, []);
  
  if (!isInitialized) {
    return (
    <div className="loader-overlay-home">
      <span className="loader" />
    </div>
    );
  }

  return (
    <div className="App">
      <ToastContainer />
      <Router>
        <AppLayout />
      </Router>
    </div>
  );
}

export default App;
